import axios from 'axios';
import { redirect } from 'helpers/common';
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
} from 'helpers/storage';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_ENDPOINTS,
  WAIBI_ACCESS_TOKEN,
  WAIBI_REFRESH_TOKEN,
} from '../config';

let isRefreshing = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;

function isValidToken(token) {
  if (!token || typeof token !== 'string') return false;
  return token.split('.').length === 3;
}

export function responseMapper(response) {
  const contentHeader =
    response.headers['content-disposition'] ||
    response.headers['Content-Disposition'];

  if (!contentHeader) {
    return Promise.resolve(response.data);
  }

  const [, fileNameInQuotes] = contentHeader.split('=');
  const fileName = decodeURI(fileNameInQuotes?.replace(/"/g, ' ').trim());

  return Promise.resolve({ fileData: response.data, fileName });
}

export function setJWTHeader() {
  return (config) => {
    if (config.url?.includes(TOKEN_ENDPOINTS.REFRESH)) {
      return config;
    }

    const accessToken = getFromStorage({
      key: ACCESS_TOKEN_KEY,
      storage: sessionStorage,
    });

    if (accessToken && isValidToken(accessToken)) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  };
}

export function setJWTLocalStorage() {
  return (response) => {
    if (
      Object.values(TOKEN_ENDPOINTS).some((endpoint) =>
        response.config.url.includes(endpoint)
      )
    ) {
      const { accessToken, refreshToken } = response?.data?.data || {};

      if (accessToken && isValidToken(accessToken)) {
        setToStorage({
          key: ACCESS_TOKEN_KEY,
          value: accessToken,
          storage: sessionStorage,
        });
      }

      if (refreshToken && isValidToken(refreshToken)) {
        setToStorage({
          key: REFRESH_TOKEN_KEY,
          value: refreshToken,
          storage: sessionStorage,
        });
      }
    }
    return response;
  };
}

export async function errorHandler(data = {}) {
  const { response, config } = data;
  const newError = {};

  if (!response) {
    return data;
  }

  const isRefreshRequest = config?.url?.includes(TOKEN_ENDPOINTS.REFRESH);

  if (response.status === 401 && isRefreshRequest) {
    removeFromStorage({
      key: [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY],
      storage: sessionStorage,
    });
    removeFromStorage({
      key: [WAIBI_ACCESS_TOKEN, WAIBI_REFRESH_TOKEN],
    });
    refreshAttempts = 0;
    redirect('/');
    return Promise.reject(data);
  }

  if (response.status === 401 && !config?._retry) {
    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      removeFromStorage({
        key: [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY],
        storage: sessionStorage,
      });
      removeFromStorage({
        key: [WAIBI_ACCESS_TOKEN, WAIBI_REFRESH_TOKEN],
      });
      refreshAttempts = 0;
      redirect('/');
      return Promise.reject(new Error('Too many refresh attempts'));
    }

    const refreshToken = getFromStorage({
      key: REFRESH_TOKEN_KEY,
      storage: sessionStorage,
    });

    if (refreshToken && isValidToken(refreshToken)) {
      if (isRefreshing) {
        await new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!isRefreshing) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        const newToken = getFromStorage({
          key: ACCESS_TOKEN_KEY,
          storage: sessionStorage,
        });
        if (newToken && isValidToken(newToken)) {
          config.headers.Authorization = `Bearer ${newToken}`;
          return axios(config);
        }
        return Promise.reject(new Error('Refresh failed'));
      }

      config._retry = true;
      isRefreshing = true;
      refreshAttempts++;

      try {
        const refreshResponse = await axios.post(TOKEN_ENDPOINTS.REFRESH, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } =
          refreshResponse.data.data || {};

        if (accessToken && isValidToken(accessToken)) {
          setToStorage({
            key: ACCESS_TOKEN_KEY,
            value: accessToken,
            storage: sessionStorage,
          });

          if (newRefreshToken && isValidToken(newRefreshToken)) {
            setToStorage({
              key: REFRESH_TOKEN_KEY,
              value: newRefreshToken,
              storage: sessionStorage,
            });
          }

          refreshAttempts = 0;
          config.headers.Authorization = `Bearer ${accessToken}`;
          return axios(config);
        }
      } catch (refreshError) {
        console.error('Refresh failed:', {
          status: refreshError.response?.status,
          message: refreshError.message,
        });

        removeFromStorage({
          key: [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY],
          storage: sessionStorage,
        });
        removeFromStorage({
          key: [WAIBI_ACCESS_TOKEN, WAIBI_REFRESH_TOKEN],
        });
        refreshAttempts = 0;
        redirect('/');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    removeFromStorage({
      key: [ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY],
      storage: sessionStorage,
    });
    removeFromStorage({
      key: [WAIBI_ACCESS_TOKEN, WAIBI_REFRESH_TOKEN],
    });
    refreshAttempts = 0;
    redirect('/');
    return Promise.reject(data);
  }

  const regularError = parseJSON(response.data && response.data.message);
  let blobError = null;

  if (!regularError) {
    if (response.data.text) {
      await response.data.text().then((value) => {
        blobError = parseJSON(value);
      });
    } else {
      blobError = response.data;
    }
  }

  newError.statusCode = response.status;
  newError.statusMessage = response.statusText;
  newError.error = regularError || blobError;

  return Promise.reject(newError);
}

function parseJSON(data) {
  try {
    return JSON.parse(data);
    // eslint-disable-next-line no-unused-vars
  } catch (e) {
    return data;
  }
}
